import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Avatar,
    Card,
    CardContent,
    Grid,
    Chip,
    Button,
} from "@mui/material";
import { styled } from "@mui/system";
import { getCustomerById } from "../services/customers";
import { useLocation, useNavigate } from "react-router-dom";
import { getCustomerAssessmentBySessionAndType } from "../services/customerAssessment";

const FormContainer = styled(Card)({
    background: "white",
    borderRadius: "20px",
    padding: "30px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
    marginBottom: "20px",
});

// Display names for supported language codes.
const languageMap = {
    eng: "English",
    kan: "Kannada",
    hin: "Hindi",
};

// Backend/patient-creation systems don't always agree on how a language is
// spelled ("Hindi", "hindi", "hi", "hin", etc). This maps any of those
// variants onto the 3-letter codes used by `languageMap` / `translations`.
const LANG_CODE_ALIASES = {
    english: "eng",
    en: "eng",
    eng: "eng",
    kannada: "kan",
    kn: "kan",
    kan: "kan",
    hindi: "hin",
    hi: "hin",
    hin: "hin",
};

const normalizeLangCode = (code) => {
    if (!code) return null;
    const key = String(code).trim().toLowerCase();
    return LANG_CODE_ALIASES[key] || key;
};

// All user-facing strings for this screen, keyed by language code.
const translations = {
    eng: {
        completeAssessments: "Complete Your Assessments",
        welcomePrefix: "Welcome,",
        asPartOf: "As part of the",
        protocolSuffix: "protocol, please complete the following assessments.",
        pleaseComplete: "Please complete the following assessments.",
        testArm: "Test Arm",
        controlArm: "Control Arm",
        pending: "Pending",
        inProgress: "In Progress",
        completed: "Completed",
        psqiTitle: "PSQI Assessment",
        psqiSubtitle: "Sleep Quality measurement",
        eortcTitle: "EORTC QLQ-C30",
        eortcSubtitle: "European Quality of Life Questionnaire",
        hadsTitle: "HADS Assessment",
        hadsSubtitle: "Hospital Anxiety and Depression Scale",
        kdqolTitle: "KDQOL-36 Assessment",
        kdqolSubtitle: "Kidney Disease and Quality of Life",
    },
    kan: {
        completeAssessments: "ನಿಮ್ಮ ಮೌಲ್ಯಮಾಪನಗಳನ್ನು ಪೂರ್ಣಗೊಳಿಸಿ",
        welcomePrefix: "ಸ್ವಾಗತ,",
        asPartOf: "ಇದರ ಭಾಗವಾಗಿ",
        protocolSuffix: "ಪ್ರೋಟೋಕಾಲ್, ದಯವಿಟ್ಟು ಈ ಕೆಳಗಿನ ಮೌಲ್ಯಮಾಪನಗಳನ್ನು ಪೂರ್ಣಗೊಳಿಸಿ.",
        pleaseComplete: "ದಯವಿಟ್ಟು ಈ ಕೆಳಗಿನ ಮೌಲ್ಯಮಾಪನಗಳನ್ನು ಪೂರ್ಣಗೊಳಿಸಿ.",
        testArm: "ಟೆಸ್ಟ್ ಆರ್ಮ್",
        controlArm: "ಕಂಟ್ರೋಲ್ ಆರ್ಮ್",
        pending: "ಬಾಕಿಯಿದೆ",
        inProgress: "ಪ್ರಗತಿಯಲ್ಲಿದೆ",
        completed: "ಪೂರ್ಣಗೊಂಡಿದೆ",
        psqiTitle: "PSQI ಮೌಲ್ಯಮಾಪನ",
        psqiSubtitle: "ನಿದ್ರೆಯ ಗುಣಮಟ್ಟ ಅಳತೆ",
        eortcTitle: "EORTC QLQ-C30",
        eortcSubtitle: "ಯುರೋಪಿಯನ್ ಜೀವನ ಗುಣಮಟ್ಟ ಪ್ರಶ್ನಾವಳಿ",
        hadsTitle: "HADS ಮೌಲ್ಯಮಾಪನ",
        hadsSubtitle: "ಆಸ್ಪತ್ರೆ ಆತಂಕ ಮತ್ತು ಖಿನ್ನತೆ ಮಾಪಕ",
        kdqolTitle: "KDQOL-36 ಮೌಲ್ಯಮಾಪನ",
        kdqolSubtitle: "ಮೂತ್ರಪಿಂಡ ರೋಗ ಮತ್ತು ಜೀವನದ ಗುಣಮಟ್ಟ",
    },
    hin: {
        completeAssessments: "अपने मूल्यांकन पूर्ण करें",
        welcomePrefix: "स्वागत है,",
        asPartOf: "के भाग के रूप में",
        protocolSuffix: "प्रोटोकॉल, कृपया निम्नलिखित मूल्यांकन पूर्ण करें।",
        pleaseComplete: "कृपया निम्नलिखित मूल्यांकन पूर्ण करें।",
        testArm: "टेस्ट आर्म",
        controlArm: "कंट्रोल आर्म",
        pending: "लंबित",
        inProgress: "प्रगति पर",
        completed: "पूर्ण",
        psqiTitle: "PSQI मूल्यांकन",
        psqiSubtitle: "नींद की गुणवत्ता मापन",
        eortcTitle: "EORTC QLQ-C30",
        eortcSubtitle: "यूरोपीय जीवन गुणवत्ता प्रश्नावली",
        hadsTitle: "HADS मूल्यांकन",
        hadsSubtitle: "अस्पताल चिंता और अवसाद स्केल",
        kdqolTitle: "KDQOL-36 मूल्यांकन",
        kdqolSubtitle: "किडनी रोग और जीवन की गुणवत्ता",
    },
};

// Turn a raw treatment_arm value ("control" / "test", any casing) into the
// translated arm label for the currently displayed language.
const formatArmLabel = (arm, t) => {
    if (!arm) return "";
    const normalized = arm.trim().toLowerCase();
    if (normalized === "control") return t.controlArm;
    if (normalized === "test") return t.testArm;
    // Fallback for any other arm value we don't have a specific translation for.
    return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)} Arm`;
};

function MyAssessments() {
    const role = localStorage.getItem("role");
    const storedCustomer = JSON.parse(localStorage.getItem("customer") || "{}");
    const { customer_id, account_id, assessment_type, preferred_language, customer_session_id } =
        storedCustomer;
    const location = useLocation();
    const pin = location.state?.pin;
    const navigate = useNavigate();

    // ----------------------- LANGUAGE -----------------------
    const rawPreferredLang = storedCustomer?.preferred_language || "eng";
    const normalizedPreferredLang = normalizeLangCode(rawPreferredLang);
    const preferredLang = translations[normalizedPreferredLang]
        ? normalizedPreferredLang
        : "eng";

    const [currentLang, setCurrentLang] = useState(preferredLang);

    const handleToggle = () => {
        setCurrentLang((prev) => (prev === "eng" ? preferredLang : "eng"));
    };

    // const t = translations[currentLang] || translations.eng;
    const t = translations.eng;

    const handleCardClick = async (item) => {
        if (item.status === "completed") {
            try {
                const response = await getCustomerAssessmentBySessionAndType(
                    customer_session_id,
                    item.key
                );
                console.log("✅ Completed Assessment Data:", response);

                // You can navigate to a detailed view page if needed:
                navigate("/assessment", { state: { data: response, assessment: item } });
            } catch (error) {
                console.error("❌ Failed to fetch assessment data:", error);
                alert("Unable to fetch assessment results. Please try again.");
            }
        }
        // 🟡 Otherwise → go to form to take assessment
        else {
            navigate("/assessment", { state: { assessment: item } });
        }
    };

    // ✅ Parse assessment_type safely
    const parsedAssessmentTypes = (() => {
        try {
            return Array.isArray(JSON.parse(assessment_type))
                ? JSON.parse(assessment_type)
                : [];
        } catch {
            return [];
        }
    })();

    // ✅ Map only matched assessments (titles/subtitles in the current language)
     const assessments = parsedAssessmentTypes
    .map((item) => {
        const title = (item.title || "").toLowerCase();

        if (title.includes("psqi")) {
            return {
                title: t.psqiTitle,
                canonicalTitle: "PSQI Assessment",
                subtitle: t.psqiSubtitle,
                key: "psqi",
                status: item.status || "pending",
            };
        }
        if (title.includes("eortc")) {
            return {
                title: t.eortcTitle,
                canonicalTitle: "EORTC QLQ-C30",
                subtitle: t.eortcSubtitle,
                key: "eortc",
                status: item.status || "pending",
            };
        }
        if (title.includes("hads")) {
            return {
                title: t.hadsTitle,
                canonicalTitle: "HADS Assessment",
                subtitle: t.hadsSubtitle,
                key: "hads",
                status: item.status || "pending",
            };
        }
        if (title.includes("kdqol")) {
            return {
                title: t.kdqolTitle,
                canonicalTitle: "KDQOL-36 Assessment",
                subtitle: t.kdqolSubtitle,
                key: "kdqol36",
                status: item.status || "pending",
            };
        }
        return null;
    })
    .filter(Boolean);
    useEffect(() => {
        if (assessments.length > 0) {
            const allCompleted = assessments.every(
                (item) => item.status === "completed"
            );
            if (allCompleted) {
                navigate("/music-therapy");
            }
        }
    }, [assessments, navigate]);


    const [customer, setCustomer] = useState(null);
    const [user, setUser] = useState("");

    useEffect(() => {
        if (customer_id && account_id) {
            getCustomerById(customer_id, account_id)
                .then((data) => {
                    setCustomer(data);
                    setUser(`${data.firstname || ""} ${data.lastname || ""}`.trim());

                    // ✅ Keep localStorage's "customer" preferred_language in sync
                    // with the patient's actual profile. The value written at
                    // login (validatePin) can be stale/wrong for some sessions
                    // (e.g. it comes back as "eng" for some Control Arm PINs even
                    // though the patient's real preferred_language is "kan"/"hin").
                    // AssessmentForm.jsx and MusicTherapy.jsx read preferred_language
                    // straight out of localStorage, so patching it here — as soon as
                    // we have the correct value — fixes the language toggle on both
                    // of those downstream screens without needing a backend change.
                    if (
                        data.preferred_language &&
                        data.preferred_language !== storedCustomer.preferred_language
                    ) {
                        const currentStoredCustomer = JSON.parse(
                            localStorage.getItem("customer") || "{}"
                        );
                        localStorage.setItem(
                            "customer",
                            JSON.stringify({
                                ...currentStoredCustomer,
                                preferred_language: data.preferred_language,
                            })
                        );
                    }
                })
                .catch((err) => {
                    console.error("Failed to fetch customer:", err);
                    setUser(
                        `${storedCustomer.firstname || ""} ${storedCustomer.lastname || ""
                            }`.trim()
                    );
                });
        }
    }, [role]);

    // ✅ Function to get chip color + translated label based on status
    const getChipColor = (status) => {
        switch (status) {
            case "completed":
                return { label: t.completed, color: "#16a34a" }; // Green
            case "in_process":
                return { label: t.inProgress, color: "#2563eb" }; // Blue
            case "pending":
            default:
                return { label: t.pending, color: "#dc2626" }; // Red
        }
    };

    // ✅ Derive the translated arm label from the fetched customer's treatment_arm field
    const armLabel = formatArmLabel(customer?.treatment_arm, t);

    return (
        <FormContainer>
            {/* Header */}
            {/* <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                }}
            >
                <Typography variant="h6" fontWeight={400} sx={{ color: "#1f2937" }}>
                    {t.completeAssessments}
                </Typography>

                {preferredLang !== "eng" && (
                    <Button
                        variant="contained"
                        onClick={handleToggle}
                        sx={{
                            bgcolor: "#1976d2",
                            "&:hover": { bgcolor: "#1565c0" },
                            borderRadius: 2,
                            px: { xs: 1.8, sm: 2.5 },
                            py: { xs: 0.9, sm: 1 },
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: { xs: "0.8rem", sm: "0.875rem" },
                            whiteSpace: "nowrap",
                        }}
                    >
                        {currentLang === "eng" ? languageMap[preferredLang] : "English"}
                    </Button>
                )}
            </Box> */}

            {/* Subtitle */}
            <Typography
                variant="subtitle1"
                sx={{ color: "#4b5563", mb: 3, lineHeight: 1.6 }}
            >
                {t.welcomePrefix} <strong>{user}</strong>!{" "}
                {armLabel ? (
                    <>
                        {t.asPartOf} <strong>{armLabel}</strong> {t.protocolSuffix}
                    </>
                ) : (
                    <>{t.pleaseComplete}</>
                )}
            </Typography>

            {/* ✅ Assessment Cards */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    mb: 4,
                }}
            >
                {assessments.map((item, index) => {
                    const chip = getChipColor(item.status);
                    return (
                        <Box
                            key={index}
                            onClick={() => handleCardClick(item)}
                            sx={{
                                position: "relative",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                p: 2.5,
                                borderRadius: 2,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                backgroundColor: "#fff",
                                border: "1.5px solid #3b82f6",
                                transition: "all 0.3s ease",
                                cursor: "pointer",
                                "&:hover": {
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                    transform: "translateY(-2px)",
                                },
                            }}
                        >
                            {/* ✅ Status Chip */}
                            <Chip
                                label={chip.label}
                                size="small"
                                sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    backgroundColor: chip.color,
                                    color: "#fff",
                                    fontWeight: 500,
                                    fontSize: "0.75rem",
                                }}
                            />

                            <Box>
                                <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                    sx={{ color: "#111827", mb: 0.5 }}
                                >
                                    {item.title}
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#6b7280" }}>
                                    {item.subtitle}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>

        </FormContainer>
    );
}

export default MyAssessments;
